import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Info, Users, Radar as RadarIcon, Filter, LayoutGrid } from 'lucide-react';
import { fetchRfInboxRequest } from '../../../redux/actions/skillEvaluationActions';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import SkillRadar from '../components/SkillRadar';
import Skill4Box from '../components/Skill4Box';
import Team4Box from '../components/Team4Box';
import '../EvaluationDesign.css';

export default function RfRadarPage() {
    const dispatch = useDispatch();
    const { inboxRf, loading } = useSelector(state => state.skillEvaluation || { inboxRf: [] });
    const { list: campagnesList } = useSelector(state => state.campagnes || { list: [] });

    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterStatus, setFilterStatus] = useState("ALL");
    const [activeTab, setActiveTab] = useState("radar"); // "radar" or "4box"

    useEffect(() => {
        dispatch(fetchRfInboxRequest());
        dispatch(fetchCampagnesRequest());
    }, [dispatch]);

    const activeCampagnes = useMemo(() => 
        (campagnesList || []).filter(c => c.status === true), 
    [campagnesList]);

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault);
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    const globalList = useMemo(() => {
        return (inboxRf || []).map(e => ({
            ...e,
            initiales: e.userFullName ? e.userFullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : "??",
            nom: e.userFullName,
            poste: e.userEmployeeCode || "Collaborateur",
            color: '#204385'
        }));
    }, [inboxRf]);

    const filteredUsers = globalList.filter(u => {
        const uStatut = typeof u.statut === 'string' ? u.statut : u.statut?.name;
        const matchSearch = u.nom?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchCampagne = !selectedCampagneId || u.campagneId === parseInt(selectedCampagneId);
        const matchStatus = filterStatus === "ALL" || uStatut === filterStatus;
        return matchSearch && matchCampagne && matchStatus;
    });

    const radarData = useMemo(() => {
        if (!selectedUser || !selectedUser.niveaux) return [];

        return selectedUser.niveaux.map(n => ({
            skill: n.skillNom,
            obtenu: n.scoreObtenu || n.scoreRf || n.scoreManager || n.scoreCollab || 0,
            requis: n.scoreRequis || 3
        }));
    }, [selectedUser]);

    return (
        <div className="eval-page">
            <div className="eval-header">
                <div>
                    <h1>Cartographie des Radars (RH)</h1>
                    <p>Visualisez les profils de compétences des collaborateurs à travers toute l'organisation.</p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <div className="eval-filter-tabs">
                        <button className={`eval-tab-btn ${filterStatus === 'ALL' ? 'active' : ''}`} onClick={() => setFilterStatus('ALL')}>Tous</button>
                        <button className={`eval-tab-btn ${filterStatus === 'VALIDEE' ? 'active' : ''}`} onClick={() => setFilterStatus('VALIDEE')}>Validés</button>
                    </div>
                </div>
            </div>

            <div className="eval-campaign-bar">
                <Filter size={16} className="text-slate-400" />
                <span className="eval-campaign-label">Campagne :</span>
                <select className="eval-select" value={selectedCampagneId} onChange={(e) => setSelectedCampagneId(e.target.value)}>
                    <option value="">-- Toutes les campagnes --</option>
                    {activeCampagnes.map(c => (
                        <option key={c.id} value={c.id}>{c.nom} {c.anneeCampagne ? `(${c.anneeCampagne})` : ''}</option>
                    ))}
                </select>
            </div>

            <div className="eval-layout">
                <aside className="eval-sidebar">
                    <div className="eval-toolbar">
                        <div className="eval-search-box">
                            <Search className="eval-search-icon" size={16} />
                            <input 
                                type="text" 
                                className="eval-input" 
                                placeholder="Rechercher un collaborateur..." 
                                value={searchQuery} 
                                onChange={(e) => setSearchQuery(e.target.value)} 
                            />
                        </div>
                    </div>
                    <div className="eval-list">
                        {loading && globalList.length === 0 ? (
                            <div className="eval-empty-small">Chargement...</div>
                        ) : filteredUsers.length > 0 ? filteredUsers.map(u => (
                            <div key={u.id} className={`eval-user-card ${selectedUser?.id === u.id ? 'active' : ''}`} onClick={() => setSelectedUser(u)}>
                                <div className="eval-avatar" style={{ backgroundColor: u.color }}>{u.initiales}</div>
                                <div className="eval-user-info">
                                    <div className="eval-user-name">{u.nom}</div>
                                    <div className="eval-user-sub">{u.poste} · {u.campagneNom}</div>
                                </div>
                            </div>
                        )) : (
                            <div className="eval-empty-small">Aucun collaborateur trouvé</div>
                        )}
                    </div>
                </aside>

                <main className="eval-main">
                    {!selectedUser ? (
                        <div className="eval-content">
                            <div className="eval-card">
                                <div className="eval-section-title">
                                    <Users size={18} /> Cartographie des Talents (4-Box Globale)
                                </div>
                                <Team4Box teamData={inboxRf} onUserSelect={setSelectedUser} />
                            </div>
                        </div>
                    ) : radarData.length > 0 ? (
                        <div className="eval-content">
                            <div className="eval-card" style={{ textAlign: 'center' }}>
                                <div className="eval-section-title" style={{ justifyContent: 'center' }}>
                                    {activeTab === 'radar' ? <RadarIcon size={18} /> : <LayoutGrid size={18} />}
                                    {activeTab === 'radar' ? "Radar Individuel" : "Matrice 4-Box Stratégique"} - {selectedUser.nom}
                                </div>

                                {/* Tabs Switcher */}
                                <div className="eval-content-tabs">
                                    <button 
                                        className={`eval-content-tab ${activeTab === 'radar' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('radar')}
                                    >
                                        <RadarIcon size={14} /> Radar
                                    </button>
                                    <button 
                                        className={`eval-content-tab ${activeTab === '4box' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('4box')}
                                    >
                                        <LayoutGrid size={14} /> Matrice 4-Box
                                    </button>
                                </div>

                                {activeTab === 'radar' ? (
                                    <>
                                        <SkillRadar data={radarData} />
                                        <div style={{ marginTop: '24px', fontSize: '13px', color: '#64748b' }}>
                                            Le polygone <strong>bleu</strong> représente les scores consolidés.<br/>
                                            La bordure <strong>corail</strong> représente le niveau attendu par le poste.
                                        </div>
                                    </>
                                ) : (
                                    <Skill4Box data={radarData} />
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="eval-empty">
                            <Info size={64} className="eval-empty-icon" />
                            <h3>Données indisponibles</h3>
                            <p>Aucune évaluation n'a été trouvée pour ce collaborateur dans les filtres sélectionnés.</p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
